import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { TwoFactorAuthService } from '@/domain/two-factor-auth/two-factor-auth.service'
import { PartnerPortalService, type PartnerUser } from '@/domain/partner-portal'
import { 
  Handshake, 
  Lock, 
  Eye, 
  EyeOff,
  ArrowRight,
  Users,
  TrendingUp,
  DollarSign,
  Smartphone,
  Loader2,
  User,
} from 'lucide-react'

export function AffiliateLogin() {
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  
  // 2FA state
  const [show2FA, setShow2FA] = useState(false)
  const [twoFactorCode, setTwoFactorCode] = useState('')
  const [pendingUserId, setPendingUserId] = useState<string | null>(null)
  const [pendingUserData, setPendingUserData] = useState<PartnerUser | null>(null)

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      // ✅ Login ผ่าน Partner Portal Service (Parse Server)
      const result = await PartnerPortalService.login(username, password)
      
      if (!result.success || !result.user) {
        setError(result.error || 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง')
        setIsLoading(false)
        return
      }
      
      const partnerUser = result.user
      
      // ✅ ตรวจสอบว่า user ต้องใช้ 2FA หรือไม่
      const twoFactorCheck = await TwoFactorAuthService.checkRequirement(partnerUser.objectId)
      console.log('🔐 [2FA] Check requirement:', twoFactorCheck)
      
      if (twoFactorCheck.requires2FA) {
        // ต้องใช้ 2FA → แสดง 2FA step
        setPendingUserId(partnerUser.objectId)
        setPendingUserData(partnerUser)
        setShow2FA(true)
        setIsLoading(false)
        return
      }
      
      // ไม่ต้องใช้ 2FA → login สำเร็จ
      completeLogin(partnerUser)
    } catch (err: any) {
      console.error('❌ Login error:', err)
      setError(err.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่')
      setIsLoading(false)
    }
  }

  const handle2FAVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!twoFactorCode.trim() || twoFactorCode.length !== 6) {
      setError('กรุณากรอกรหัสยืนยัน 6 หลัก')
      return
    }
    
    if (!pendingUserId) {
      setError('ไม่พบข้อมูลผู้ใช้')
      return
    }
    
    setIsLoading(true)
    setError('')
    
    try {
      // ตรวจสอบ TOTP code
      const verifyResult = await TwoFactorAuthService.verifyToken(
        pendingUserId,
        twoFactorCode.trim()
      )
      
      if (!verifyResult.verified) {
        setError(verifyResult.error || 'รหัสยืนยันไม่ถูกต้อง')
        setIsLoading(false)
        return
      }
      
      // 2FA ถูกต้อง → login สำเร็จ
      if (pendingUserData) {
        completeLogin(pendingUserData)
      }
      
    } catch (err: any) {
      console.error('❌ 2FA verify error:', err)
      setError(err.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่')
      setIsLoading(false)
    }
  }

  const completeLogin = (_userData: PartnerUser) => {
    // Session ถูกเก็บแล้วใน PartnerPortalService.login()
    navigate('/affiliate')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-500/10" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
        
        <div className="relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/30 mb-8">
            <Handshake className="h-10 w-10" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">
            Affiliate Portal
          </h1>
          <p className="text-xl text-slate-400 mb-12 max-w-md">
            ระบบจัดการพันธมิตร สร้างรายได้ง่ายๆ ด้วยการแนะนำเพื่อน
          </p>

          <div className="grid grid-cols-3 gap-6">
            <div className="p-4 rounded-xl bg-white/5 backdrop-blur border border-white/10">
              <Users className="h-8 w-8 text-indigo-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">10K+</p>
              <p className="text-xs text-slate-400">พันธมิตร</p>
            </div>
            <div className="p-4 rounded-xl bg-white/5 backdrop-blur border border-white/10">
              <TrendingUp className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">35%</p>
              <p className="text-xs text-slate-400">คอมมิชชั่นสูงสุด</p>
            </div>
            <div className="p-4 rounded-xl bg-white/5 backdrop-blur border border-white/10">
              <DollarSign className="h-8 w-8 text-amber-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">฿50M+</p>
              <p className="text-xs text-slate-400">จ่ายแล้ว</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <Card className="w-full max-w-md bg-card/50 backdrop-blur border-border/50">
          <CardHeader className="text-center pb-2">
            <div className="lg:hidden inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white mx-auto mb-4">
              <Handshake className="h-7 w-7" />
            </div>
            <CardTitle className="text-2xl">
              {show2FA ? 'ยืนยันตัวตน 2 ขั้นตอน' : 'เข้าสู่ระบบพันธมิตร'}
            </CardTitle>
            <CardDescription>
              {show2FA 
                ? 'กรอกรหัสจากแอป Authenticator'
                : 'กรอกข้อมูลเพื่อเข้าสู่ระบบ Affiliate Portal'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!show2FA ? (
              <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="username">ชื่อผู้ใช้</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="username"
                    type="text"
                    placeholder="username"
                    className="pl-10"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">รหัสผ่าน</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="remember" 
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                  />
                  <Label htmlFor="remember" className="text-sm font-normal cursor-pointer">
                    จดจำการเข้าสู่ระบบ
                  </Label>
                </div>
                <Button variant="link" className="px-0 text-sm">
                  ลืมรหัสผ่าน?
                </Button>
              </div>

              <Button type="submit" className="w-full gap-2" disabled={isLoading}>
                {isLoading ? (
                  <>กำลังเข้าสู่ระบบ...</>
                ) : (
                  <>
                    เข้าสู่ระบบ
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>

              <div className="text-center pt-4 border-t">
                <p className="text-sm text-muted-foreground mb-2">
                  ยังไม่มีบัญชีพันธมิตร?
                </p>
                <Button variant="outline" className="w-full">
                  สมัครเป็นพันธมิตร
                </Button>
              </div>
            </form>
            ) : (
              <form onSubmit={handle2FAVerify} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="twoFactorCode">รหัสยืนยัน 6 หลัก</Label>
                  <Input
                    id="twoFactorCode"
                    type="text"
                    placeholder="000000"
                    value={twoFactorCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setTwoFactorCode(value);
                    }}
                    disabled={isLoading}
                    autoComplete="one-time-code"
                    className="text-center text-2xl tracking-widest font-mono"
                    maxLength={6}
                  />
                  <p className="text-xs text-muted-foreground text-center">
                    เปิดแอป Authenticator (Google Authenticator, Authy) และกรอกรหัส 6 หลัก
                  </p>
                </div>

                {error && (
                  <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm">
                    {error}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShow2FA(false);
                      setTwoFactorCode('');
                      setPendingUserId(null);
                      setPendingUserData(null);
                      setError('');
                    }}
                    disabled={isLoading}
                  >
                    ย้อนกลับ
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 gap-2"
                    disabled={isLoading || twoFactorCode.length !== 6}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        กำลังตรวจสอบ...
                      </>
                    ) : (
                      <>
                        <Smartphone className="h-4 w-4" />
                        ยืนยัน
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}










